import React, { useState, useEffect } from "react";
import { tablesDB, DB_ID, PRODUCTS_TABLE } from "../../appwrite";
import { Query } from "appwrite";
import "./ShopCategory.css";
import Product from "../Product/Product";
import { useTitle } from "../../hooks/useTitle";
import SEO from "../../Components/SEO/SEO";
import Footer from "../../Components/Footer/Footer";
import Navbar from "../../Components/Navbar/Navbar";
import SideBar from "../Product/SideBar";
import Sorting from "../Shop/Sorting";
import Loading from "../../Loading";
import { useParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";

function fmtCat(slug) {
  if (!slug) return "";
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function slugify(text) {
  if (!text) return "";
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/&/g, "and")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-");
}

export default function ShopCategory({ category }) {
  const url = window.location.pathname;
  const match = url.match(/\/shop\/([^/]+)/);
  const queryCategoryName = match ? match[1] : "all";
  const displayName = fmtCat(match ? match[1].replace(/-/g, " ") : "All");

  const { encoded_seller_id, seller_name } = useParams();

  useTitle(`${displayName} - TrendBay`);

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sort, setSort] = useState("newest");
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const fmtSeller = (slug) =>
    (slug || "")
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const queries = [Query.equal("category", queryCategoryName)];
        let sellerId = "";
        try {
          sellerId = encoded_seller_id ? atob(encoded_seller_id) : "";
        } catch {}
        if (sellerId) queries.push(Query.equal("sellerId", sellerId));

        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: PRODUCTS_TABLE,
          queries,
        });
        const rows = (res.rows || res.documents || []).map((r) => ({
          id: r.$id,
          ...r,
        }));
        setProducts(rows);
        setFilteredProducts(rows);
      } catch {
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [queryCategoryName, encoded_seller_id]);

  useEffect(() => {
    let list = [...products];
    if (filters.minPrice !== undefined)
      list = list.filter((p) => p.price >= filters.minPrice);
    if (filters.maxPrice !== undefined)
      list = list.filter((p) => p.price <= filters.maxPrice);
    if (filters.availability)
      list = list.filter((p) => p.available === filters.availability);

    switch (sort) {
      case "price-asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        list.sort((a, b) => b.price - a.price);
        break;
      case "popularity":
        list.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
        break;
      default:
        list.sort(
          (a, b) =>
            new Date(b.$createdAt || b.createdAt || "") -
            new Date(a.$createdAt || a.createdAt || ""),
        );
    }
    setFilteredProducts(list);
  }, [filters, products, sort]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const pageProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage,
  );

  const catLabel = fmtCat(category);
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mart.trendbayds.com" },
      { "@type": "ListItem", "position": 2, "name": "Shop", "item": "https://mart.trendbayds.com/shop" },
      { "@type": "ListItem", "position": 3, "name": catLabel, "item": `https://mart.trendbayds.com/shop/${category}` },
    ],
  };

  return (
    <div className="sc-page">
      <SEO
        title={`${catLabel} — Shop Online`}
        description={`Browse ${catLabel} on TrendBay Mart. Fresh stock, competitive prices, and fast delivery across Nigeria.`}
        path={`/shop/${category}`}
        jsonLd={breadcrumbSchema}
      />
      <Navbar />

      <div className="sc-wrap">
        {/* Breadcrumb */}
        <nav className="sc-crumb">
          <a href="/">Home</a>
          <span>/</span>
          <a href="/shop">Shop</a>
          <span>/</span>
          <span className="sc-crumb-active">
            {displayName}
            {encoded_seller_id && seller_name && ` (${fmtSeller(seller_name)})`}
          </span>
        </nav>

        <h1 className="sc-title">
          {displayName}
          {encoded_seller_id && seller_name && ` - ${fmtSeller(seller_name)}`}
        </h1>

        {/* Mobile filter bar - sticky below page title */}
        <div className="sc-mob-filter-bar">
          <button
            className="sc-mob-filter-bar__btn"
            onClick={() => setFilterOpen(true)}
          >
            <SlidersHorizontal size={15} /> Filter
          </button>
          <div className="sc-mob-filter-bar__sort">
            <Sorting onSortChange={setSort} />
          </div>
          <span className="sc-mob-filter-bar__count">
            {filteredProducts.length} items
          </span>
        </div>

        <div className="sc-layout">
          <aside className="sc-sidebar">
            <SideBar
              onFilterChange={(f) => {
                setFilters(f);
                setCurrentPage(1);
              }}
            />
          </aside>

          <div className="sc-main">
            <div className="sc-toolbar">
              <Sorting onSortChange={setSort} />
            </div>

            {loading ? (
              <div className="sc-loading">
                <Loading />
              </div>
            ) : pageProducts.length > 0 ? (
              <div className="sc-grid">
                {pageProducts.map((p) => (
                  <Product
                    key={p.id}
                    product_id={p.id}
                    productImage={p.productImage}
                    productName={p.productName}
                    price={p.price}
                    productDescription={p.productDescription}
                    category={p.category}
                  />
                ))}
              </div>
            ) : (
              <div className="sc-empty">
                <p>
                  No products found in <strong>{displayName}</strong>.
                </p>
              </div>
            )}

            {!loading && totalPages > 1 && (
              <div className="sc-pagination">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    className={`sc-page-btn${i + 1 === currentPage ? " active" : ""}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter bottom sheet */}
      {filterOpen && (
        <>
          <div
            className="sc-sheet-backdrop"
            onClick={() => setFilterOpen(false)}
          />
          <div className="sc-sheet">
            <div className="sc-sheet-top">
              <div className="sc-sheet-handle" />
              <button
                className="sc-sheet-close"
                onClick={() => setFilterOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="sc-sheet-body">
              <SideBar
                onFilterChange={(f) => {
                  setFilters(f);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="sc-sheet-footer">
              <button
                className="sc-sheet-apply"
                onClick={() => setFilterOpen(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
      <Footer />
    </div>
  );
}
